import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'

import { authInstance } from '@/http/axios/instances/auth-service'
import { cisInstance } from '@/http/axios/instances/cis-service'
import { documentsInstance } from '@/http/axios/instances/documents-service'
import { schedulerInstance } from '@/http/axios/instances/scheduler-service'
import { refreshToken } from '@/modules/authentication/authenticationService'
import { getBearerToken } from '@/services/localStorage'

export * from './auth-service'
export * from './cis-service'
export * from './documents-service'
export * from './scheduler-service'

export const axiosInstances = {
  $auth: authInstance,
  $cis: cisInstance,
  $documents: documentsInstance,
  $scheduler: schedulerInstance,
}

interface RetryQueueItem {
  resolve: (value?: any) => void
  reject: (error?: any) => void
  config: AxiosRequestConfig & { _retry?: boolean }
}

const refreshAndRetryQueue: RetryQueueItem[] = []
let isRefreshing = false
const disabledRequests = ['auth/login', 'auth/loginAsUser', 'auth/refreshtoken']

function setupInterceptors(instance: AxiosInstance) {
  instance.interceptors.request.use(
    (config) => {
      const token = getBearerToken()
      if (token) {
        config.headers = config.headers || {}
        config.headers.Authorization = token
      }
      return config
    },
    error => Promise.reject(error),
  )

  instance.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const config = error.config as AxiosRequestConfig & { _retry?: boolean }
      if (!config)
        return Promise.reject(error)

      if (error.response?.status === 401 && !disabledRequests.includes(config.url as string) && !config._retry) {
        if (!isRefreshing) {
          isRefreshing = true
          config._retry = true

          try {
            await refreshToken()
            const newToken = getBearerToken()

            refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
              config.headers = config.headers || {}
              config.headers.Authorization = newToken
              instance.request(config).then(resolve).catch(reject)
            })
            refreshAndRetryQueue.length = 0

            config.headers = config.headers || {}
            config.headers.Authorization = newToken
            return instance.request(config)
          }
          catch (refreshError) {
            return Promise.reject(refreshError)
          }
          finally {
            isRefreshing = false
          }
        }
        else {
          return new Promise((resolve, reject) => {
            refreshAndRetryQueue.push({ config, resolve, reject })
          })
        }
      }

      return Promise.reject(error)
    },
  )
}

Object.values(axiosInstances).forEach(setupInterceptors)
